import { Globals } from '../../modules/Globals';
import axios from 'axios';
import { IOrder, ITableSessionEmbed } from '../../interfaces/IOrder';
import { IPaginated } from '../../interfaces/IPaginated';
import { IDataOptions } from '../../interfaces/IDataOptions';
import {
  OrderMode,
  OrderPaymentStatus,
  OrderStatus,
} from '../../enums/OrderEnums';
import { IOrderItem } from '../../interfaces/IOrderItem';
import { PaymentMethod } from '../../enums/PaymentMethod';
import IModifyOrderDto from '../../interfaces/dtos/orders/IModifyOrderDto';
import { IInvoicingItem } from '@/interfaces/IInvoicingItem';
import { TInvoiceIssueResponse } from '@/interfaces/IInvoicingAttempt';
import { ICajaMovement } from '@/interfaces/ICajaMovement';
import { serializeQuery } from '@/utils';
import { IDataOptions } from '@/interfaces/IDataOptions';
import { encodeBase64 } from '@/utils/b64.utils';
import { IAuthData } from '@/interfaces/IAuthData';
import { useSocketStore } from '@/composition/use-socket';
import { useOfflineStore } from '@/stores/offline.store';
import { requestWithRetry } from '@/api/retry.utils';

const apiUrl = Globals.apiPath + '/orders';
const v2ApiUrl = Globals.apiPath + '/v2/orders';
const tableSessionsApiUrl = Globals.apiPath + '/v2/table-sessions';

export interface ICreateOrderData {
  restaurantId: number;
  tableId?: number;
  branchId: number;
  comment: string;
  type: string;
  paymentType: string;
  paymentMethod: PaymentMethod;
  items: IOrderItem[];
  userAddress?: string;
  userAddress2?: string;
  userAddressComments?: string;
  deliveryLat?: number;
  deliveryLng?: number;
  userName?: string;
  userWhatsapp?: string;
  userTel?: string;
  mode: OrderMode;
  humanId?: number;
  manual?: boolean;
  deviceId: string;
  cajaId?: number | null;
}

export interface IUpdateOrderData {
  comment?: string;
  items?: IOrderItem[];
  userAddress?: string;
  userAddress2?: string;
  userAddressComments?: string;
  deliveryLat?: number;
  deliveryLng?: number;
  userName?: string;
  userWhatsapp?: string;
  userTel?: string;
}

interface IDoPaymentDto {
  orderId: number;
  amount: number;
  paymentMethod: PaymentMethod;
  cajaId?: number;
  comment?: string;
  customerId?: number;
}

interface IDoMassPaymentDto {
  orderIds: number[];
  amount: number;
  paymentMethod: PaymentMethod;
  cajaId?: number;
  comment?: string;
  customerId?: number;
}

/** Additive GET /orders filters (unified orders-history page). */
export interface IGetAllOrdersFilters {
  /** 0 → only settled orders (server-side generated `pending` column). */
  pending?: 0;
  /** Free text: id/humanId/phone/address digits, names, items, table. */
  search?: string;
  minTotal?: number;
  maxTotal?: number;
  /**
   * ISO instants (always via toISOString(), never date-only strings).
   * dateTo is exclusive; the server applies a 90-day fallback lower bound
   * when dateFrom is absent.
   */
  dateFrom?: string;
  dateTo?: string;
  /**
   * Skips the expensive COUNT(*) (total comes back as -1). Used by
   * load-more pages, which reuse the total fetched with page 1.
   */
  skipCount?: boolean;
  /**
   * Loads each order's item lines (`orderItems`, canceled lines included)
   * in one extra query on the server. Used by the GPTHelp `orders_find` tool.
   */
  includeItems?: boolean;
}

export default class OrdersService {
  constructor() {}

  async getAll(
    restaurantId: number,
    branchId: number,
    status: string[] = null,
    tables: number[] = null,
    types: string[] = null,
    options: IDataOptions | null = null,
    filters: IGetAllOrdersFilters = null
  ): Promise<IPaginated<IOrder>> {
    let path = `${apiUrl}?r=${restaurantId}&b=${branchId}`;

    if (status && status.length > 0) path += `&status=${status.join(',')}`;

    if (types && types.length > 0) path += `&types=${types.join(',')}`;

    if (tables && tables.length > 0) path += `&tables=${tables.join(',')}`;

    if (options) path += `&options=${JSON.stringify(options)}`;

    if (filters) {
      if (filters.pending === 0) path += '&pending=0';
      if (filters.search && filters.search.trim())
        path += `&search=${encodeURIComponent(filters.search.trim())}`;
      if (typeof filters.minTotal === 'number')
        path += `&minTotal=${filters.minTotal}`;
      if (typeof filters.maxTotal === 'number')
        path += `&maxTotal=${filters.maxTotal}`;
      if (filters.dateFrom)
        path += `&dateFrom=${encodeURIComponent(filters.dateFrom)}`;
      if (filters.dateTo)
        path += `&dateTo=${encodeURIComponent(filters.dateTo)}`;
      if (filters.skipCount) path += '&skipCount=1';
      if (filters.includeItems) path += '&includeItems=1';
    }

    let res = await axios.get<IPaginated<IOrder>>(path);
    return res.data;
  }

  static async getNewAndPending(
    restaurantId: number,
    branchId: number
  ): Promise<IOrder[]> {
    let path = `${apiUrl}/newAndPending?r=${restaurantId}&b=${branchId}`;
    let res = await axios.get<IOrder[]>(path);
    return res.data;
  }

  /** Batch fetch by ids (tenant-scoped server-side; foreign ids are dropped). */
  static async getByIds(
    restaurantId: number,
    ids: number[]
  ): Promise<IOrder[]> {
    if (!ids.length) return [];
    const res = await axios.get<IOrder[]>(
      `${apiUrl}/ids?r=${restaurantId}&ids=${ids.join(',')}`
    );
    return res.data;
  }

  static async getById(id: number): Promise<IOrder> {
    const random = Math.round(Math.random() * 1000000); // To avoid Axios not making the calls concurrently
    let res = await axios.get<IOrder>(`${apiUrl}/${id}?iq=${random}`);
    return res.data;
  }

  async create(order: ICreateOrderData): Promise<IOrder> {
    let res = await axios.post<IOrder>(apiUrl, order);
    return res.data;
  }

  async update(id: number, order: IUpdateOrderData): Promise<IOrder> {
    let res = await axios.put<IOrder>(`${apiUrl}/${id}`, order);
    return res.data;
  }

  async getOrderItems(id: number): Promise<IOrderItem[]> {
    let res = await axios.get<IOrderItem[]>(`${apiUrl}/${id}/items`);
    return res.data;
  }

  static async modify(orderId: number, data: IModifyOrderDto): Promise<IOrder> {
    let res = await axios.post<IOrder>(`${apiUrl}/${orderId}/modify`, data);
    return res.data;
  }

  static async setStatus(
    orderId: number,
    status: OrderStatus,
    cajaId?: number,
    authData?: IAuthData
  ): Promise<IOrder> {
    let res = await axios.put<IOrder>(`${apiUrl}/${orderId}/setStatus`, {
      status,
      cajaId,
      authData,
    });
    return res.data;
  }

  /**
   * May resolve with the invoicing item (emitted within the request) or a
   * 202 `processing` body when the attempt is parked for background
   * settlement — discriminate with `isInvoiceProcessingResponse`.
   */
  static async createInvoice(
    orderId: number,
    invoiceData: {
      invoiceType: number;
      idType: number;
      id: string;
      idempotencyKey?: string;
      acceptPending?: boolean;
    }
  ): Promise<TInvoiceIssueResponse> {
    let res = await axios.post<TInvoiceIssueResponse>(
      `${apiUrl}/${orderId}/invoice`,
      invoiceData
    );
    return res.data;
  }

  /** Same 200-item / 202-processing contract as `createInvoice`. */
  static async massCreateInvoice(
    orderIds: number[],
    invoiceData: { invoiceType: number; idType?: number; id?: string },
    options?: { idempotencyKey?: string; acceptPending?: boolean }
  ): Promise<TInvoiceIssueResponse> {
    let res = await axios.post<TInvoiceIssueResponse>(`${apiUrl}/massInvoice`, {
      orderIds,
      invoiceType: invoiceData.invoiceType,
      // Without a recipient document the emission targets final consumer;
      // the API rejects non-C invoices that carry no idType.
      idType: invoiceData.idType ?? 99,
      id: invoiceData.id,
      idempotencyKey: options?.idempotencyKey,
      acceptPending: options?.acceptPending,
    });
    return res.data;
  }

  static async doPayment({
    orderId,
    amount,
    paymentMethod,
    cajaId,
    comment,
    customerId,
  }: IDoPaymentDto): Promise<IOrder> {
    let res = await axios.post<IOrder>(`${apiUrl}/${orderId}/doPayment`, {
      amount,
      paymentMethod,
      cajaId,
      comment,
      customerId,
    });
    return res.data;
  }

  static async doMassPayment({
    orderIds,
    amount,
    paymentMethod,
    cajaId,
    comment,
    customerId,
  }: IDoMassPaymentDto): Promise<IOrder[]> {
    let res = await axios.post<IOrder[]>(`${apiUrl}/doMassPayment`, {
      orderIds,
      amount,
      paymentMethod,
      cajaId,
      comment,
      customerId,
    });
    return res.data;
  }

  static async getCajaMovements(orderId: number): Promise<ICajaMovement[]> {
    let res = await axios.get<ICajaMovement[]>(
      `${apiUrl}/${orderId}/movements`
    );
    return res.data;
  }

  static async setDiscount({
    orderId,
    discount,
  }: {
    orderId: number;
    discount: number;
  }): Promise<IOrder> {
    let res = await axios.put<IOrder>(`${apiUrl}/${orderId}/discount`, {
      discount,
    });
    return res.data;
  }

  static async massSetStatus(
    orderIds: number[],
    status: OrderStatus,
    cajaId?: number
  ): Promise<IOrder> {
    let res = await axios.put<IOrder>(`${apiUrl}/setStatus`, {
      orderIds,
      status,
      cajaId,
    });
    return res.data;
  }

  /* async setPaymentStatus(
    orderId: number,
    status: OrderPaymentStatus
  ): Promise<IOrder> {
    let res = await axios.put<IOrder>(`${apiUrl}/${orderId}/setPaymentStatus`, {
      status,
    });
    return res.data;
  } */

  /* async massSetPaymentStatus(
    orderIds: number[],
    status: OrderPaymentStatus
  ): Promise<IOrder> {
    let res = await axios.put<IOrder>(`${apiUrl}/setPaymentStatus`, {
      orderIds,
      status,
    });
    return res.data;
  } */

  static async massMove(orderIds: number[], toTableId: number): Promise<void> {
    let res = await axios.put<void>(`${apiUrl}/move`, {
      orderIds,
      toTableId,
    });
    return res.data;
  }

  async delete(order: IOrder): Promise<IOrder> {
    let res = await axios.put<IOrder>(`${apiUrl}/${order.id}`);
    return res.data;
  }

  //#region V2

  static async v2Create(data: any): Promise<IOrder> {
    const socket = useSocketStore();
    if (!socket.isConnected && Globals.offlineOrderingEnabled) {
      return (useOfflineStore() as any).offlineCreateOrder(data);
    }

    // Creates are idempotent server-side on localId, so a short timeout plus
    // bounded retry recovers from Heroku's 30s router cutoff / network drops
    // instead of hanging the POS spinner forever (POS create-stall incident).
    const res = await requestWithRetry(() =>
      axios.post<IOrder>(`${v2ApiUrl}`, data, { timeout: 15000 })
    );
    return res.data;
  }

  static async v2Update(id: number, data: any): Promise<IOrder> {
    const socket = useSocketStore();
    if (!socket.isConnected && Globals.offlineOrderingEnabled) {
      const offlineStore = useOfflineStore() as any;
      const updatedOrder = offlineStore.offlineUpdateOrder(id, data);
      if (!updatedOrder) {
        throw new Error('Order not found in offline store');
      }
      return updatedOrder;
    }

    const res = await axios.put<IOrder>(`${v2ApiUrl}/${id}`, data);
    return res.data;
  }

  /**
   * Staff covers edit on an open table session. Unlike v2Update there is NO
   * offline fallback branch — offline just surfaces the network error toast.
   */
  static async patchTableSessionCovers(
    tableSessionId: string,
    covers: number | null
  ): Promise<ITableSessionEmbed> {
    const res = await axios.patch<ITableSessionEmbed>(
      `${tableSessionsApiUrl}/${tableSessionId}`,
      { covers }
    );
    return res.data;
  }

  /** Orders of a (closed) table session — post-close surfaces. */
  static async getByTableSession(tableSessionId: string): Promise<IOrder[]> {
    const res = await axios.get<IOrder[]>(
      `${apiUrl}/tableSession/${tableSessionId}`
    );
    return res.data;
  }

  static async v2GetOrder(hash: string): Promise<IOrder> {
    const res = await axios.get<IOrder>(`${v2ApiUrl}/h/${hash}`);

    if (res.data.orderItems) {
      res.data.orderItems = res.data.orderItems.sort((a, b) =>
        a.index > b.index ? 1 : -1
      );
    }

    return res.data;
  }

  static async v2GetOrderById(
    restaurantId: number,
    id: number
  ): Promise<IOrder> {
    const hash = encodeBase64(`${restaurantId}-${id}`);

    const res = await axios.get<IOrder>(`${v2ApiUrl}/h/${hash}`);

    if (res.data.orderItems) {
      res.data.orderItems = res.data.orderItems.sort((a, b) =>
        a.index > b.index ? 1 : -1
      );
    }

    return res.data;
  }

  static async v2GetOrders({
    onlyPending = true,
    dataOptions,
    restaurantId,
    branchId,
    tableToken,
    updatedSinceMinutes,
  }: {
    onlyPending?: boolean;
    dataOptions?: IDataOptions;
    restaurantId?: number;
    branchId?: number;
    tableToken?: string;
    /** Widens onlyPending with recently-updated orders (print catch-up). */
    updatedSinceMinutes?: number;
  }): Promise<IPaginated<IOrder>> {
    const res = await axios.get<IPaginated<IOrder>>(
      `${v2ApiUrl}?${serializeQuery({
        dataOptions: dataOptions ? JSON.stringify(dataOptions) : undefined,
        onlyPending,
        tableToken,
        restaurantId,
        branchId,
        updatedSinceMinutes,
      })}`
    );

    for (const order of res.data.items) {
      if (order.orderItems) {
        order.orderItems = order.orderItems.sort((a, b) =>
          a.index > b.index ? 1 : -1
        );
      }
    }

    return res.data;
  }

  static async v2MarkPrinted({
    orderId,
    indexes,
    printT,
    printC,
    printE,
    printedPaid,
  }: {
    orderId: number;
    indexes: number[];
    printT?: number[];
    printC?: number[];
    printE?: number[];
    printedPaid?: boolean;
  }): Promise<IOrder> {
    // TODO: Should auto-retry on failure
    const res = await axios.put<IOrder>(`${v2ApiUrl}/${orderId}/markPrinted`, {
      indexIds: indexes,
      printT,
      printC,
      printE,
      printedPaid,
    });

    return res.data;
  }
}
